import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { capi, apiError } from "@/lib/api";
import { useCustomer } from "@/context/CustomerContext";
import { AuthCard, FieldLabel, Notice, inputCls, btnCls } from "@/components/account/AuthCard";

export default function AccountLogin() {
  const { login } = useCustomer();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [unverified, setUnverified] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setUnverified(false);
    try {
      await login(f.email, f.password);
      const next = params.get("next");
      navigate(next && next.startsWith("/") && !next.startsWith("//") ? next : "/account");
    } catch (e2) {
      setErr(apiError(e2, "Sign in failed"));
      setUnverified(e2?.response?.status === 403);
    } finally { setBusy(false); }
  };

  const resend = async () => {
    try {
      await capi.post("/account/resend-verification", { email: f.email, origin: window.location.origin });
      toast.success("If your account is unverified, a new link is on its way.");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <AuthCard title="Sign in" subtitle="Track your custom orders, message us, and pay for approved designs." testid="account-login-page">
      <form onSubmit={submit}>
        {err && <Notice testid="account-login-error">{err}</Notice>}
        {unverified && (
          <button type="button" data-testid="resend-verification-btn" onClick={resend} className="mb-4 text-xs font-semibold text-kys-accent hover:underline">
            Resend verification email
          </button>
        )}
        <FieldLabel>Email</FieldLabel>
        <input data-testid="account-login-email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={`${inputCls} mb-4`} />
        <FieldLabel>Password</FieldLabel>
        <input data-testid="account-login-password" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={`${inputCls} mb-2`} />
        <div className="mb-5 text-right">
          <Link to="/account/forgot" data-testid="forgot-password-link" className="text-xs text-kys-muted hover:text-kys-accent">Forgot password?</Link>
        </div>
        <button data-testid="account-login-submit" disabled={busy} className={btnCls}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
        </button>
      </form>
      <p className="mt-6 text-center text-sm text-kys-muted">
        New here? <Link to="/account/register" data-testid="go-register-link" className="font-semibold text-kys-accent hover:underline">Create an account</Link>
      </p>
    </AuthCard>
  );
}
